import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    Tooltip,
    Legend,
    PointElement,
    Filler,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import React, { FC } from 'react';
import { Line } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import { colors, gutterSize, fontFamilyPrimary } from '../../../theme';
import { evolutionIconPlugin, getEvolution, withEvolutionMarker } from './evolution';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    Tooltip,
    Legend,
    ChartDataLabels,
    Filler,
);

type LineChartIndicatorProps = {
    listOfValues: any
}

enum legendPosition {
    BOTTOM = 'bottom'
}

enum secondXAxePosition {
    TOP = 'top'
}

enum legendAlign {
    START = 'start'
}

const COMPARISON_LINE_DASH = [6, 6];

const LineChartIndicator: FC<LineChartIndicatorProps> = ({ listOfValues }) => {
    const { t } = useTranslation('dashboard');
    const { labels } = listOfValues;
    const comparisonResults = listOfValues.comparison?.results;
    const comparisonLabel = listOfValues.comparison?.type === 'TEMPORAL' ? t('compareToGlobal.temporalLegend') : t('compareToGlobal.legend');
    // One dashed grey counterpart per curve. Its name stays unambiguous when the
    // chart draws several modalities at once.
    const comparisonDatasets = comparisonResults
        ? listOfValues.datasets.map((dataset: any) => ({
            label: listOfValues.datasets.length > 1
                ? `${comparisonLabel} - ${dataset.label}`
                : comparisonLabel,
            data: labels.map((waveLabel: string) => comparisonResults[waveLabel]?.data?.[dataset.label] ?? null),
            borderColor: colors.grey500,
            borderDash: COMPARISON_LINE_DASH,
            fill: false,
            pointBorderColor: colors.grey500,
            pointBackgroundColor: colors.grey500,
        }))
        : [];
    const options = {
        indexAxis: 'x' as const,
        responsive: true,
        layout: {
            padding: {
                left: gutterSize.m,
                right: gutterSize.xxl,
                top: 10,
            },
        },
        clip: false as const,
        plugins: {
            legend: {
                // Hidden until a second curve makes it necessary to tell them apart.
                display: comparisonDatasets.length > 0,
                position: legendPosition.BOTTOM,
                align: legendAlign.START,
                fullSize: true,
                labels: {
                    boxWidth: 32,
                    boxHeight: 2,
                    padding: 20,
                    color: colors.grey200,
                    font: {
                        family: fontFamilyPrimary,
                        size: 14,
                    },
                },
                maxHeight: 1200,
                maxWidth: 200,
            },
            tooltip: {
                enabled: true,
                callbacks: {
                    label: (dataset: any) => {
                        // Only the main curves carry a verdict, the compared ones are their reference.
                        const isMainCurve = dataset.datasetIndex < listOfValues.datasets.length;
                        const evolution = isMainCurve
                            ? getEvolution(
                                listOfValues.comparison?.type,
                                comparisonResults?.[dataset.label]?.significance,
                                dataset.dataset.label,
                            )
                            : null;
                        const tooltipLabel = withEvolutionMarker(
                            `${dataset.dataset.label}: ${dataset.raw.toFixed(listOfValues.round)}${listOfValues.computeMethod === 'PERCENT' ? '%' : ''}`,
                            evolution,
                        );

                        return [`Base : ${listOfValues.bases[dataset.dataIndex]}`, tooltipLabel];
                    },
                },
            },
            datalabels: {
                display: false,
            },
        },
        scales: {
            x: {
                display: true,
                grid: {
                    display: true,
                    drawBorder: false,
                    drawTicks: true,
                    offset: false,
                    tickLength: 30,
                    color: colors.grey700,
                },
                ticks: {
                    color: colors.grey200,
                    font: {
                        size: 15,
                        family: 'Barlow',
                    },
                },
                scale: {
                    padding: 50,
                },
            },
            x2: {
                display: true,
                grid: {
                    display: true,
                    drawBorder: false,
                    drawTicks: true,
                    offset: false,
                    tickLength: 30,
                    color: colors.grey700,
                    drawLenght: 20,
                },
                ticks: {
                    display: false,
                },
                position: secondXAxePosition.TOP,
            },
            y: {
                display: true,
                grid: {
                    display: true,
                    drawBorder: false,
                    drawTicks: true,
                    offset: false,
                    tickLength: 30,
                    color: colors.grey700,
                },
                ticks: {
                    color: colors.grey200,
                    font: {
                        size: 15,
                        family: 'Barlow',
                    },
                    fontColor: colors.grey400,
                    callback: (value: any) => {
                        const tickLabel = listOfValues.computeMethod === 'PERCENT' ? `${value.toFixed(listOfValues.round)}%` : value;

                        return tickLabel;
                    },
                },
                min: listOfValues.min,
                max: listOfValues.max,
            },
        },
        elements: {
            point: {
                radius: 3,
                borderColor: colors.blue000,
                backgroundColor: colors.blue000,
            },
            line: {
                fill: true,
                borderColor: colors.blue000,
                backgroundColor: (context: any) => {
                    const bgColor = [
                        colors.blue000RGBA,
                        'rgba(255,255,255,0.25)',
                    ];
                    if (!context.chart.chartArea) {
                        return false;
                    }
                    const { ctx, chartArea: { top, bottom } } = context.chart;
                    const gradienBg = ctx.createLinearGradient(0, top, 0, bottom);
                    gradienBg.addColorStop(0, bgColor[0]);
                    gradienBg.addColorStop(1, bgColor[1]);

                    return gradienBg;
                },
            },
        },
    };

    const data = {
        labels,
        datasets: [...listOfValues.datasets, ...comparisonDatasets],
    };

    return (
        <Line
            data={data}
            options={options}
            plugins={[evolutionIconPlugin]}
            style={{ maxHeight: '600px' }}
        />
    );
};

export default LineChartIndicator;
